/**
 * Stop Time Analysis
 * Analys av fordonsstopptider för planering av laddinfrastruktur
 */

import React, { useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { PageBackButton } from '@/components/common/PageBackButton';
import { useTranslation } from '@/store/languageStore';
import { stopTimeAnalysisService, StopTimeAnalysisParams } from '@/services/analysis/stopTimeAnalysisService';
import { StopTimeAnalysisResult } from '@/types/analysis';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import toast from 'react-hot-toast';
import StopTimeHeatmap from './StopTimeHeatmap';
import StopTimeMap from './StopTimeMap';

import DateInput from '@/components/common/DateInput';

type ViewMode = 'heatmap' | 'map';

const StopTimeAnalysis: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('heatmap');
  const [data, setData] = useState<StopTimeAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  // const [vehicleIds, setVehicleIds] = useState<string[]>([]); // TODO: Add vehicle filter

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: StopTimeAnalysisParams = {
        startDate,
        endDate,
        organizationId,
        // vehicleIds: vehicleIds.length > 0 ? vehicleIds : undefined, // TODO: Add vehicle filter
      };

      const result = await stopTimeAnalysisService.getStopTimeAnalysis(params);
      setData(result);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Kunde inte hämta data';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Data hämtas endast när användaren klickar på "Uppdatera"-knappen
  // Ingen automatisk hämtning vid sidladdning

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <PageBackButton />
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ClockIcon className="w-6 h-6 text-primary" />
            {t('analysis.stopTime.title')}
          </h1>
        </div>
        <p className="text-textSecondary">
          {t('analysis.stopTime.description')}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-bgElevated rounded-lg p-4 mb-6 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('analysis.stopTime.startDate')}
            </label>
            <DateInput value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-bgContainer text-textDefault"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('analysis.stopTime.endDate')}
            </label>
            <DateInput value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-bgContainer text-textDefault"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('analysis.stopTime.organization')}
            </label>
            <HierarchicalOrgSelect
              value={organizationId ?? ''}
              onChange={(id) => setOrganizationId(id || undefined)}
              placeholder={t('analysis.stopTime.selectOrganization')}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('analysis.stopTime.loading') : t('analysis.stopTime.update')}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-bgElevated rounded-lg p-4 border border-border">
            <div className="text-sm text-textSecondary mb-1">{t('analysis.stopTime.totalLocations')}</div>
            <div className="text-2xl font-bold text-textDefault">
              {data.summary.totalLocations.toLocaleString('sv-SE')}
            </div>
          </div>
          <div className="bg-bgElevated rounded-lg p-4 border border-border">
            <div className="text-sm text-textSecondary mb-1">{t('analysis.stopTime.totalHours')}</div>
            <div className="text-2xl font-bold text-textDefault">
              {Math.round(data.summary.totalHours).toLocaleString('sv-SE')} h
            </div>
          </div>
          <div className="bg-bgElevated rounded-lg p-4 border border-border">
            <div className="text-sm text-textSecondary mb-1">{t('analysis.stopTime.totalStops')}</div>
            <div className="text-2xl font-bold text-textDefault">
              {data.summary.totalStops.toLocaleString('sv-SE')}
            </div>
          </div>
          <div className="bg-bgElevated rounded-lg p-4 border border-border">
            <div className="text-sm text-textSecondary mb-1">{t('analysis.stopTime.vehiclesAnalyzed')}</div>
            <div className="text-2xl font-bold text-textDefault">
              {data.summary.totalVehicles}
            </div>
            {data.summary.organizationName && (
              <div className="text-xs text-textSecondary mt-1">
                {data.summary.organizationName}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <div className="mb-4">
        <div className="flex space-x-2 border-b border-border">
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-4 py-2 font-medium transition-colors ${
              viewMode === 'heatmap'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-textSecondary hover:text-textDefault'
            }`}
          >
            {t('analysis.stopTime.tabHeatmap')}
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`px-4 py-2 font-medium transition-colors ${
              viewMode === 'map'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-textSecondary hover:text-textDefault'
            }`}
          >
            {t('analysis.stopTime.tabMap')}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {viewMode === 'heatmap' && <StopTimeHeatmap data={data} />}
          {viewMode === 'map' && <StopTimeMap data={data} />}
        </>
      )}

      {!loading && !error && !data && (
        <div className="text-center py-12 text-textSecondary">
          {t('analysis.stopTime.emptyState')}
        </div>
      )}
    </div>
  );
};

export default StopTimeAnalysis;

